import { appConfigStore } from 'Models/AppConfigStore';
import { ILink } from 'Views/Components/Navigation/Navigation';
import { store } from 'Models/Store';
import React from 'react';
import { forceExpireSession } from 'Services/Api/AuthorizationService';
import showLightNavBarOnPage from 'Views/Components/Navigation/NavigationUtils';
import { Search } from 'Views/Components/_HumanWritten/Search/Search';
import { bookingsListUrl } from 'Util/_HumanWritten/Navigation/bookingsListUrl';
import { getFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { LanguageNavBarButton } from './Components/_HumanWritten/Languages/LanguageNavBarButton';
import {
	getCurrentLanguageName,
	getLanguageCount,
	getLanguages,
	getMessageByKey,
	switchLanguagesAsync,
} from 'Stores/LanguageStore';
import OrganisationNavBarButton from './Components/_HumanWritten/Organisations/OrganisationNavBarButton';
import { OrganisationEntity } from '../Models/Entities';
import { themeStore } from 'Models/ThemeStore';
import { FormattedMessage } from 'react-intl';
import { ROUTES, WLIDS } from 'Constants';
import { Help } from './Components/_HumanWritten/Help/Help';
import { Text } from './Components/Text/Text';
import { Location } from 'react-router-dom';
import alertToast from 'Util/ToastifyUtils';
import { anytimeTicketsLabel } from 'Util/_HumanWritten/anytimeTicketTerm';

export function getFrontendNavLinks(
	location: Location,
	isMobile: boolean,
	organisations: OrganisationEntity[] | null,
	selectedOrganisation: OrganisationEntity | null,
	bookingsInCart: boolean,
): ILink[][] {
	const multiOrg = (organisations?.length ?? 1) > 1;
	const userGroups = store.userGroups.length ? store.userGroups.map(group => group.name) : [];

	let userProfileClassName: string;

	if (store.userData?.isGuest) {
		userProfileClassName = 'guest-circle-background';
	} else if (userGroups.includes('Admin')) {
		userProfileClassName = 'admin-circle-background';
	} else if (userGroups.includes('Staff') || userGroups.includes('Manager')) {
		userProfileClassName = 'staff-circle-background';
	} else if (
		userGroups.includes('User') ||
		userGroups.includes('FixedPriceUser') ||
		userGroups.includes('InvoicedUser')
	) {
		userProfileClassName = 'customer-circle-background';
	} else {
		userProfileClassName = 'user-circle-background';
	}

	const onEmailConfirmPage = location.pathname.includes('/register');
	const { eventsEnabled } = appConfigStore;

	const currentLang = getCurrentLanguageName();
	const languagesDropdownMenuItems = getLanguages().map(x => {
		return {
			label: x.name,
			path: '#',
			className: x.name === currentLang ? 'active' : '',
			innerTagClass: 'hide-underline',
			isDisabled: false,
			onClick: (event: React.MouseEvent<HTMLElement>) => {
				event?.preventDefault();
				event?.stopPropagation();
				switchLanguagesAsync(x.id);
			},
		};
	});

	languagesDropdownMenuItems.unshift({
		label: 'Select language',
		path: '#',
		className: 'select-language__label',
		innerTagClass: 'hide-underline',
		isDisabled: true,
		onClick: (event: React.MouseEvent<HTMLElement>) => {
			// Do nothing
		},
	});

	const getStaffDropdownMenuItems = () => {
		// Pre-calculate conditions for readability
		const showSchedule = isMobile && (!multiOrg || (multiOrg && isNotNullOrUndefined(selectedOrganisation?.id)));
		const showEvents = isMobile && (eventsEnabled || (selectedOrganisation?.hasTours ?? false));

		const items = [
			...(showSchedule
				? [
						{
							label: 'Schedule',
							path: '/ferry-schedule',
							innerTagClass: 'hide-underline',
						},
					]
				: []),
			...(showEvents
				? [
						{
							label: getMessageByKey('navigation__events__nav-link__label', 'Events'),
							path: '/upcoming-events',
							innerTagClass: 'hide-underline',
						},
					]
				: []),
			...(isMobile
				? [
						{
							label: 'Bookings',
							path: '/staff-bookings',
							innerTagClass: 'hide-underline',
						},
						...(appConfigStore.anytimeTicketingEnabled
							? [
									{
										label: anytimeTicketsLabel(),
										path: '/anytime-tickets',
										innerTagClass: 'hide-underline',
									},
								]
							: []),
						{
							label: 'Transactions',
							path: '/transactions',
							innerTagClass: 'hide-underline',
						},
						{
							label: 'Users',
							path: ROUTES.USERS,
							innerTagClass: 'hide-underline',
						},
						{
							label: 'Admin',
							path: ROUTES.ADMIN.HOME,
							innerTagClass: 'hide-underline',
						},
					]
				: []),
			{
				label: 'Account',
				path: '/account-details',
				innerTagClass: 'hide-underline',
			},
			{
				label: 'Logout',
				path: '/logout',
				innerTagClass: 'hide-underline',
			},
		];

		return items.filter(Boolean);
	};

	const staffDropdownMenuItems = getStaffDropdownMenuItems();

	const getCustomerDropdownMenuItems = () => {
		const showEvents = isMobile && (eventsEnabled || (selectedOrganisation?.hasTours ?? false));

		return [
			...(!store.userData?.isGuest
				? [
						{
							label: 'Bookings',
							// Shared with anything sending a reader back to their bookings — see the helper.
							path: bookingsListUrl(),
							innerTagClass: 'hide-underline',
						},
					]
				: []),
			...(isMobile
				? [
						{
							label: 'Schedule',
							path: '/ferry-schedule',
							innerTagClass: 'hide-underline',
						},
					]
				: []),
			...(showEvents
				? [
						{
							label: getMessageByKey('navigation__events__nav-link__label', 'Events'),
							path: '/upcoming-events',
							innerTagClass: 'hide-underline',
						},
					]
				: []),
			...(!store.userData?.isGuest
				? [
						{
							label: 'Account',
							path: '/account-details/account',
							innerTagClass: 'hide-underline',
						},
					]
				: []),
			...(appConfigStore.showCreditOnCustomerAccount && !store.userData?.isGuest
				? [
						{
							label: 'Credits',
							path: '/account-details/credits',
							innerTagClass: 'hide-underline',
						},
					]
				: []),
			{
				label: store.userData?.isGuest ? 'End session' : 'Logout',
				path: '/logout',
				innerTagClass: 'hide-underline',
			},
			...(process.env.NODE_ENV !== 'production'
				? [
						{
							label: 'Force Expire',
							path: '#',
							customComponent: <span className="hide-underline nav__link">Force expire</span>,
							onClick: (event: React.MouseEvent<HTMLElement>) => {
								event?.preventDefault();
								event?.stopPropagation();
								forceExpireSession()
									.then(() => {
										alertToast('Session force expired', 'success');
									})
									.catch(() => {
										alertToast('Failed to force expire session', 'error');
									});
							},
						},
					]
				: []),
		];
	};

	const customerDropdownMenuItems = getCustomerDropdownMenuItems();

	if ((store.isInvoicedUser || store.isFixedPriceUser) && !isMobile) {
		customerDropdownMenuItems.shift();
	}
	return [
		[
			{
				label: (
					<OrganisationNavBarButton
						transparent={
							location.pathname.includes('/home') ||
							(location.pathname.includes('/booking-wizard') && !isMobile) ||
							location.pathname.includes('/booking-success') ||
							location.pathname.includes('/booking-cancelled')
						}
					/>
				),
				path: '#',
				className: 'organisation-icon',
				innerTagClass: 'hide-underline',
				shouldDisplay: () => {
					return (
						isMobile &&
						!location?.pathname.toString().includes('login') &&
						!location?.pathname.toString().includes('register') &&
						multiOrg
					);
				},
				//
				// Don't allow user to leave VerifyEmailPage or EmailConfirmationPage
				//
				isDisabled: onEmailConfirmPage,
			},
			{
				label: (() => {
					const isLight = showLightNavBarOnPage(isMobile, location);
					let logoUrl: string;
					if (isMobile) {
						logoUrl = isLight ? themeStore.logoSecondaryLightURL : themeStore.logoSecondaryURL;
					} else {
						logoUrl = isLight ? themeStore.logoPrimaryLightURL : themeStore.logoPrimaryDarkURL;
					}
					return <img className="nav__logo" src={logoUrl} alt="company-logo" title="Home" />;
				})(),

				// If the application is MULTI ORG and the user hasn't selected an organisation, then show the home page
				// OR if the application is MULTI ORG and the user has selected an organisation that has ferries, then show the home page
				// If the application is SINGLE ORG always show the home page
				path:
					!multiOrg ||
					isNullOrUndefined(selectedOrganisation?.id) ||
					(isNotNullOrUndefined(selectedOrganisation?.id) && selectedOrganisation?.hasFerries)
						? '/home'
						: '/upcoming-events',
				innerTagClass: 'hide-underline',
				className: `li__logo ${onEmailConfirmPage ? 'disabled' : ''}`,
				//
				// Don't allow user to leave VerifyEmailPage or EmailConfirmationPage
				//
				isDisabled: onEmailConfirmPage,
			},
			{
				label: (
					<OrganisationNavBarButton
						transparent={
							location.pathname.includes('/default') ||
							(location.pathname.includes('/booking-wizard') && !isMobile) ||
							location.pathname.includes('/booking-success') ||
							location.pathname.includes('/booking-cancelled')
						}
					/>
				),
				path: '#',
				className: 'organisation-icon',
				innerTagClass: 'hide-underline',
				shouldDisplay: () => {
					return (
						!isMobile &&
						!location?.pathname.toString().includes('login') &&
						!location?.pathname.toString().includes('register') &&
						multiOrg
					);
				},
				//
				// Don't allow user to leave VerifyEmailPage or EmailConfirmationPage
				//
				isDisabled: onEmailConfirmPage,
			},
		],
		[
			{
				label: <Search />,
				path: '', // The search component will prevent propagation to this nav component, so the path is irrelevant
				shouldDisplay: () => store.isStaff,
				//
				// Don't display on VerifyEmailPage
				//
				innerTagClass: 'hide-underline',
			},
			{
				label: <Text size="xs">Schedule</Text>,
				path: '/ferry-schedule',
				className:
					location && location.pathname === '/ferry-schedule' ? 'current-page-nav-link' : 'page-nav-link',
				innerTagClass: 'hide-underline',
				shouldDisplay: () =>
					(selectedOrganisation?.hasFerries ?? true) &&
					!isMobile &&
					//
					// Don't display on VerifyEmailPage
					//
					(!multiOrg || (multiOrg && isNotNullOrUndefined(selectedOrganisation?.id))),
			},
			{
				label: (
					<Text size="xs">
						<FormattedMessage id="navigation__events__nav-link__label" defaultMessage="Events" />
					</Text>
				),
				path: '/upcoming-events',
				className:
					location && location.pathname === '/upcoming-events' ? 'current-page-nav-link' : 'page-nav-link',
				innerTagClass: 'hide-underline',
				shouldDisplay: () => (eventsEnabled || (selectedOrganisation?.hasTours ?? false)) && !isMobile,
				//
				// Don't display on VerifyEmailPage
				//
			},
			{
				label: <Text size="xs">Bookings</Text>,
				path: '/staff-bookings',
				className:
					location && location.pathname === '/staff-bookings' ? 'current-page-nav-link' : 'page-nav-link',
				innerTagClass: 'hide-underline',
				shouldDisplay: () =>
					(store.isStaff || store.isInvoicedUser || store.isFixedPriceUser) &&
					!isMobile &&
					//
					// Don't display on VerifyEmailPage
					//
					(!multiOrg || (multiOrg && isNotNullOrUndefined(selectedOrganisation?.id))),
			},
			{
				label: <Text size="xs">Transactions</Text>,
				path: '/transactions',
				className:
					location && location.pathname === '/transactions' ? 'current-page-nav-link' : 'page-nav-link',
				innerTagClass: 'hide-underline',
				// Staff can read transactions; the actions on them (change fees, bulk selection) stay
				// manager-only inside the table itself.
				shouldDisplay: () =>
					store.isStaff &&
					!isMobile &&
					(!multiOrg || (multiOrg && isNotNullOrUndefined(selectedOrganisation?.id))),
			},
			{
				label: <Text size="xs">Users</Text>,
				path: ROUTES.USERS,
				className: location && location.pathname === '/users' ? 'current-page-nav-link' : 'page-nav-link',
				innerTagClass: 'hide-underline',
				shouldDisplay: () =>
					store.isStaff &&
					!isMobile &&
					(!multiOrg || (multiOrg && isNotNullOrUndefined(selectedOrganisation?.id))),
			},
			{
				label: <Text size="xs">Admin</Text>,
				path: '/admin',
				className: location && location.pathname === '/admin' ? 'current-page-nav-link' : 'page-nav-link',
				innerTagClass: 'hide-underline',
				shouldDisplay: () =>
					store.isStaff &&
					!isMobile &&
					(!multiOrg || (multiOrg && isNotNullOrUndefined(selectedOrganisation?.id))),
			},
			{
				shouldDisplay: () => store.isStaff && !isMobile,
				path: store.configuration.knowledgeBaseLink,
				customComponent: <Help path={store.configuration.knowledgeBaseLink} />,
				label: 'Help',
			},
			{
				label: '',
				path: '/booking-wizard/cart',
				icon: 'cart',
				iconPos: 'icon-right',
				className: `cart-item ${isMobile ? 'mobile' : ''}`,
				innerTagClass: 'hide-underline',
				showRedDot: true,
				shouldDisplay: () => {
					const transactionId = getFerryBookingTransactionIdFromStorage();
					// The cart opens the booking wizard, which staff without BookOnBehalfOfCustomer can't use.
					const result =
						store.mayMakeBookings &&
						isNotNullOrUndefined(transactionId) &&
						(store.loggedIn ||
							(!store.loggedIn &&
								(location.pathname.includes('/booking-wizard') ||
									location.pathname.includes('/ferry-schedule')))) &&
						!location.pathname.includes('post-payment') &&
						bookingsInCart;
					return result;
				},
			},
			{
				label: (
					<LanguageNavBarButton
						transparent={
							location.pathname.includes('/home') ||
							(location.pathname.includes('/booking-wizard') && !isMobile) ||
							location.pathname.includes('/booking-success') ||
							location.pathname.includes('/booking-cancelled')
						}
					/>
				),
				path: '/languages',
				shouldDisplay: () => {
					return (
						getLanguageCount() > 1 &&
						!location?.pathname.toString().includes('login') &&
						!location?.pathname.toString().includes('register')
					);
				},
				className: 'language-icon',
				innerTagClass: 'hide-underline',
				subLinks: languagesDropdownMenuItems,
			},
			{
				label: (
					<div className={userProfileClassName}>
						{store.userData?.isGuest ? (
							<FormattedMessage {...WLIDS.guest.guest} />
						) : (
							<>{store.userInitials ?? 'SA'}</>
						)}
					</div>
				),
				path: '/profile',
				className: 'profile-icon-right',
				innerTagClass: 'hide-underline',
				shouldDisplay: () => store.loggedIn,
				//
				// Hide profile link on verify email page
				//
				subLinks: store.isStaff ? staffDropdownMenuItems : customerDropdownMenuItems,
			},
			{
				label: <Text size="xs">Login/Sign up</Text>,
				path: '/login',
				innerTagClass: 'hide-underline',
				className: 'page-nav-link login-signup-link',
				shouldDisplay: () =>
					!store.loggedIn &&
					!location?.pathname.toString().includes('login') &&
					!location?.pathname.toString().includes('register'),
			},
		],
	];
}
